import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '@/App';
import { useNavigate } from 'react-router-dom';
import Header from '@/components/shared/Header';
import Footer from '@/components/shared/Footer';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  BookOpen, 
  Clock, 
  Award, 
  GraduationCap,
  IndianRupee,
  CheckCircle,
  AlertCircle,
  PlayCircle,
  FileQuestion,
  Trophy
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AssessmentPortalPage = () => {
  const { user, isAuthenticated } = useContext(AuthContext);
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [myEnrollments, setMyEnrollments] = useState([]);
  const [myResults, setMyResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [enrollType, setEnrollType] = useState('STT');
  const [isEnrollDialogOpen, setIsEnrollDialogOpen] = useState(false);
  const [isMockTestDialogOpen, setIsMockTestDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('courses');

  useEffect(() => {
    fetchData();
  }, [isAuthenticated]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const coursesRes = await axios.get(`${API}/assessment/courses?status=active`);
      setCourses(coursesRes.data);

      if (isAuthenticated) {
        const [enrollRes, resultsRes] = await Promise.all([
          axios.get(`${API}/assessment/my-enrollments`),
          axios.get(`${API}/assessment/my-results`)
        ]);
        setMyEnrollments(enrollRes.data);
        setMyResults(resultsRes.data);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const isEnrolled = (courseId) => {
    return myEnrollments.some(e => e.course_id === courseId);
  };

  const getEnrollment = (courseId) => {
    return myEnrollments.find(e => e.course_id === courseId);
  };

  const handleEnrollClick = (course) => {
    if (!isAuthenticated) {
      toast.error('Please login to enroll');
      navigate('/auth/login');
      return;
    }
    setSelectedCourse(course);
    setEnrollType('STT');
    setIsEnrollDialogOpen(true);
  };

  const handleEnroll = async () => {
    const amount = enrollType === 'STT' ? selectedCourse.stt_fees : selectedCourse.rpl_fees;
    
    // Demo student - skip payment completely
    const demoEmails = ['demo_test@iqra.com', 'test_student@iqra.com', 'demo@iqra.com'];
    const isDemoStudent = user?.email && demoEmails.includes(user.email.toLowerCase());
    
    if (isDemoStudent) {
      try {
        // Direct enrollment without payment for demo students
        await axios.post(`${API}/assessment/enroll`, {
          course_id: selectedCourse.id,
          enrollment_type: enrollType
        });
        
        toast.success('Demo Enrollment Successful! Ab aap exam de sakte hain.');
        setIsEnrollDialogOpen(false);
        fetchData();
        return;
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Enrollment failed');
        return;
      }
    }
    
    try {
      // Create order
      const orderRes = await axios.post(`${API}/payment/create-order`, {
        course_id: selectedCourse.id,
        enrollment_type: enrollType,
        amount: amount
      });
      
      const orderData = orderRes.data;
      
      if (orderData.demo_mode) {
        // Demo mode - simulate payment
        const paymentRes = await axios.post(`${API}/payment/verify`, {
          razorpay_order_id: orderData.order_id,
          razorpay_payment_id: `demo_pay_${Date.now()}`,
          razorpay_signature: 'demo_signature',
          course_id: selectedCourse.id,
          enrollment_type: enrollType
        });
        
        toast.success('Enrolled successfully! (Demo Mode)');
        setIsEnrollDialogOpen(false);
        fetchData();
        return;
      }
      
      // Real Razorpay payment
      const options = {
        key: orderData.key_id,
        amount: orderData.amount,
        currency: orderData.currency,
        name: 'Iqra Skills',
        description: `${enrollType} Enrollment - ${selectedCourse.name}`,
        order_id: orderData.order_id,
        handler: async function (response) {
          try {
            await axios.post(`${API}/payment/verify`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              course_id: selectedCourse.id,
              enrollment_type: enrollType
            });
            
            toast.success('Payment successful! Enrolled in course.');
            setIsEnrollDialogOpen(false);
            fetchData();
          } catch (error) {
            toast.error('Payment verification failed');
          }
        },
        prefill: {
          name: user?.name || '',
          email: user?.email || '',
          contact: user?.phone || ''
        },
        theme: {
          color: '#2563eb'
        }
      };
      
      const razorpay = new window.Razorpay(options);
      razorpay.open();
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create payment');
    }
  };

  const handleStartExam = async (courseId) => {
    const enrollment = getEnrollment(courseId);
    if (!enrollment) {
      toast.error('Please enroll first');
      return;
    }
    navigate(`/assessment/exam/${courseId}/${enrollment.id}`);
  };

  const handleMockTestClick = (course) => {
    if (!isAuthenticated) {
      toast.error('Please login to take mock test');
      navigate('/auth/login');
      return;
    }
    setSelectedCourse(course);
    setIsMockTestDialogOpen(true);
  };

  const handleStartMockTest = () => {
    setIsMockTestDialogOpen(false);
    navigate(`/assessment/mock-test/${selectedCourse.id}`);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-blue-600 to-emerald-600 rounded-2xl p-8 text-white mb-8">
          <div className="max-w-3xl">
            <h1 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Online Assessment & Certification Portal
            </h1>
            <p className="text-lg text-blue-100 mb-6">
              Get certified in your skills. Take online MCQ exams, get instant results, 
              and download your certificate immediately upon passing.
            </p>
            <div className="flex flex-wrap gap-4 text-sm">
              <div className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full">
                <Clock className="h-4 w-4" />
                <span>45 Minutes Exam</span>
              </div>
              <div className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full">
                <CheckCircle className="h-4 w-4" />
                <span>50% Passing Score</span>
              </div>
              <div className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full">
                <Award className="h-4 w-4" />
                <span>Instant Certificate</span>
              </div>
              <div className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full">
                <FileQuestion className="h-4 w-4" />
                <span>Free Mock Tests</span>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full max-w-md grid-cols-3">
            <TabsTrigger value="courses">Courses</TabsTrigger>
            <TabsTrigger value="mock-tests">Mock Tests</TabsTrigger>
            <TabsTrigger value="my-exams">My Exams</TabsTrigger>
          </TabsList>

          {/* Courses Tab */}
          <TabsContent value="courses">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Available Assessment Courses</h2>
            
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto"></div>
                <p className="text-slate-600 mt-4">Loading courses...</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {courses.map((course) => (
                  <div 
                    key={course.id} 
                    className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-lg transition-shadow"
                    data-testid={`course-card-${course.id}`}
                  >
                    <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-4">
                      <GraduationCap className="h-10 w-10 text-white mb-2" />
                      <h3 className="text-xl font-bold text-white">{course.name}</h3>
                    </div>
                    
                    <div className="p-6">
                      <p className="text-slate-600 text-sm mb-4 line-clamp-2">{course.description}</p>
                      
                      <div className="space-y-2 mb-4">
                        <div className="flex items-center text-sm text-slate-600">
                          <Clock className="h-4 w-4 mr-2" />
                          Duration: {course.duration}
                        </div>
                        <div className="flex items-center text-sm text-slate-600">
                          <BookOpen className="h-4 w-4 mr-2" />
                          Qualification: {course.qualification_required}
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-2 mb-4 p-3 bg-slate-50 rounded-lg">
                        <div className="text-center">
                          <p className="text-xs text-slate-500">STT Fee</p>
                          <p className="font-bold text-blue-600 flex items-center justify-center">
                            <IndianRupee className="h-3 w-3" />
                            {course.stt_fees.toLocaleString()}
                          </p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-slate-500">RPL Fee</p>
                          <p className="font-bold text-emerald-600 flex items-center justify-center">
                            <IndianRupee className="h-3 w-3" />
                            {course.rpl_fees.toLocaleString()}
                          </p>
                        </div>
                      </div>

                      {isEnrolled(course.id) ? (
                        <Button 
                          className="w-full bg-emerald-600 hover:bg-emerald-700"
                          disabled
                        >
                          <CheckCircle className="h-4 w-4 mr-2" />
                          Already Enrolled
                        </Button>
                      ) : (
                        <Button 
                          className="w-full bg-blue-600 hover:bg-blue-700"
                          onClick={() => handleEnrollClick(course)}
                          data-testid={`enroll-btn-${course.id}`}
                        >
                          Enroll Now
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Mock Tests Tab */}
          <TabsContent value="mock-tests">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Practice Mock Tests</h2>
              <p className="text-slate-600">
                Take unlimited practice tests to prepare for your certification exam. 
                Mock test results don't affect your official certification.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 flex items-start gap-3">
              <FileQuestion className="h-5 w-5 text-amber-600 mt-0.5" />
              <div>
                <p className="font-medium text-amber-800">Practice Mode</p>
                <p className="text-sm text-amber-700">
                  Mock tests are free and unlimited. Results are shown immediately but no certificate is issued.
                  Use these to prepare for your actual certification exam.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {courses.map((course) => (
                <div 
                  key={course.id} 
                  className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-lg transition-shadow"
                  data-testid={`mock-test-card-${course.id}`}
                >
                  <div className="bg-gradient-to-r from-purple-500 to-purple-600 p-4">
                    <FileQuestion className="h-10 w-10 text-white mb-2" />
                    <h3 className="text-xl font-bold text-white">{course.name}</h3>
                    <span className="inline-block mt-2 px-2 py-1 bg-white/20 rounded text-xs text-white">
                      MOCK TEST
                    </span>
                  </div>
                  
                  <div className="p-6">
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center text-sm text-slate-600">
                        <Clock className="h-4 w-4 mr-2" />
                        Duration: 45 minutes
                      </div>
                      <div className="flex items-center text-sm text-slate-600">
                        <BookOpen className="h-4 w-4 mr-2" />
                        Questions: 40 MCQs
                      </div>
                      <div className="flex items-center text-sm text-slate-600">
                        <Trophy className="h-4 w-4 mr-2" />
                        Passing: 50%
                      </div>
                    </div>

                    <div className="bg-purple-50 p-3 rounded-lg mb-4 text-center">
                      <p className="text-purple-700 font-medium">FREE</p>
                      <p className="text-xs text-purple-600">Unlimited Attempts</p>
                    </div>

                    <Button 
                      className="w-full bg-purple-600 hover:bg-purple-700"
                      onClick={() => handleMockTestClick(course)}
                      data-testid={`mock-test-btn-${course.id}`}
                    >
                      <PlayCircle className="h-4 w-4 mr-2" />
                      Start Mock Test
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* My Exams Tab */}
          <TabsContent value="my-exams">
            {!isAuthenticated ? (
              <div className="text-center py-12 bg-white rounded-xl border">
                <GraduationCap className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-600 mb-4">Please login to view your exams</p>
                <Button onClick={() => navigate('/auth/login')}>Login</Button>
              </div>
            ) : myEnrollments.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl border">
                <BookOpen className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-600 mb-4">You haven't enrolled in any courses yet</p>
                <Button onClick={() => setActiveTab('courses')}>Browse Courses</Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {myEnrollments.map((enrollment) => {
                  const course = courses.find(c => c.id === enrollment.course_id);
                  if (!course) return null;
                  
                  const result = myResults.find(r => r.course_id === enrollment.course_id);
                  
                  return (
                    <div key={enrollment.id} className="bg-white rounded-xl border border-slate-200 p-6">
                      <h3 className="font-bold text-slate-900 mb-2">{course.name}</h3>
                      <p className="text-sm text-slate-600 mb-4">{enrollment.enrollment_type} Program</p>
                      
                      {result ? (
                        <div className={`p-4 rounded-lg ${result.result === 'pass' ? 'bg-emerald-50' : 'bg-red-50'}`}>
                          <div className="flex items-center gap-2 mb-2">
                            {result.result === 'pass' ? (
                              <CheckCircle className="h-5 w-5 text-emerald-600" />
                            ) : (
                              <AlertCircle className="h-5 w-5 text-red-600" />
                            )}
                            <span className={`font-semibold ${result.result === 'pass' ? 'text-emerald-700' : 'text-red-700'}`}>
                              {result.result === 'pass' ? 'PASSED' : 'FAILED'}
                            </span>
                          </div>
                          <p className="text-sm text-slate-600">Score: {result.percentage.toFixed(1)}%</p>
                          {result.result === 'pass' && (
                            <Button 
                              className="w-full mt-3 bg-emerald-600 hover:bg-emerald-700"
                              onClick={() => navigate(`/assessment/certificate/${result.certificate_id}`)}
                            >
                              <Award className="h-4 w-4 mr-2" />
                              View Certificate
                            </Button>
                          )}
                        </div>
                      ) : (
                        <Button 
                          className="w-full bg-blue-600 hover:bg-blue-700"
                          onClick={() => handleStartExam(course.id)}
                          data-testid={`start-exam-${course.id}`}
                        >
                          <PlayCircle className="h-4 w-4 mr-2" />
                          Start Exam
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Enrollment Dialog */}
      <Dialog open={isEnrollDialogOpen} onOpenChange={setIsEnrollDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Enroll in {selectedCourse?.name}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-slate-600 text-sm">{selectedCourse?.description}</p>
            
            <div className="space-y-2">
              <p className="font-medium text-slate-900">Select Program Type:</p>
              
              <label className={`flex items-center justify-between p-4 border rounded-lg cursor-pointer ${enrollType === 'STT' ? 'border-blue-600 bg-blue-50' : 'border-slate-200'}`}>
                <div className="flex items-center">
                  <input
                    type="radio"
                    name="enrollType"
                    value="STT"
                    checked={enrollType === 'STT'}
                    onChange={(e) => setEnrollType(e.target.value)}
                    className="mr-3"
                  />
                  <div>
                    <p className="font-medium">STT (Short Term Training)</p>
                    <p className="text-sm text-slate-500">Complete training program with certification</p>
                  </div>
                </div>
                <span className="font-bold text-blue-600">
                  ₹{selectedCourse?.stt_fees?.toLocaleString()}
                </span>
              </label>
              
              <label className={`flex items-center justify-between p-4 border rounded-lg cursor-pointer ${enrollType === 'RPL' ? 'border-blue-600 bg-blue-50' : 'border-slate-200'}`}>
                <div className="flex items-center">
                  <input
                    type="radio"
                    name="enrollType"
                    value="RPL"
                    checked={enrollType === 'RPL'}
                    onChange={(e) => setEnrollType(e.target.value)}
                    className="mr-3"
                  />
                  <div>
                    <p className="font-medium">RPL (Recognition of Prior Learning)</p>
                    <p className="text-sm text-slate-500">For candidates with existing skills</p>
                  </div>
                </div>
                <span className="font-bold text-emerald-600">
                  ₹{selectedCourse?.rpl_fees?.toLocaleString()}
                </span>
              </label>
            </div>

            <div className="bg-slate-50 p-4 rounded-lg">
              <p className="text-sm text-slate-600 mb-2">Exam Details:</p>
              <ul className="text-sm space-y-1">
                <li>• 40 Multiple Choice Questions</li>
                <li>• 45 Minutes Duration</li>
                <li>• 50% Passing Score Required</li>
                <li>• Instant Result & Certificate</li>
              </ul>
            </div>

            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setIsEnrollDialogOpen(false)}>
                Cancel
              </Button>
              <Button className="flex-1 bg-blue-600 hover:bg-blue-700" onClick={handleEnroll}>
                Pay ₹{enrollType === 'STT' ? selectedCourse?.stt_fees?.toLocaleString() : selectedCourse?.rpl_fees?.toLocaleString()}
              </Button>
            </div>
            
            <p className="text-xs text-slate-500 text-center">
              * Payment integration coming soon. Demo mode enabled.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Mock Test Dialog */}
      <Dialog open={isMockTestDialogOpen} onOpenChange={setIsMockTestDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileQuestion className="h-5 w-5 text-purple-600" />
              Mock Test: {selectedCourse?.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-purple-50 p-4 rounded-lg">
              <p className="font-medium text-purple-800 mb-2">Practice Mode</p>
              <p className="text-sm text-purple-700">
                This is a practice test. Your results will be shown but no certificate will be issued.
              </p>
            </div>

            <div className="bg-slate-50 p-4 rounded-lg">
              <p className="text-sm text-slate-600 mb-2">Test Details:</p>
              <ul className="text-sm space-y-1">
                <li>• 40 Random Questions from question bank</li>
                <li>• 45 Minutes Duration</li>
                <li>• 50% Passing Score</li>
                <li>• Instant Results</li>
                <li>• Unlimited Attempts</li>
              </ul>
            </div>

            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setIsMockTestDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                className="flex-1 bg-purple-600 hover:bg-purple-700" 
                onClick={handleStartMockTest}
                data-testid="start-mock-test-confirm"
              >
                <PlayCircle className="h-4 w-4 mr-2" />
                Start Mock Test
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default AssessmentPortalPage;
